import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

/**
 * 3D Model Viewer with three.js
 *
 * This script handles the loading and interactive display of GLB models
 * with 360-degree rotation capabilities.
 */
export default class ModelViewer {
	constructor( container ) {
		// Store references to DOM elements
		this.container = container;
		this.canvas = container.querySelector( '#model-canvas' );
		this.modelUrl = container.dataset.modelUrl;
		this.hqModelUrl = container.dataset.hqModelUrl;
		this.shouldLoadHq = container.dataset.loadHq === 'true';
		this.loadingSpinner = container.querySelector( '.loading-spinner' );
		this.rotateIndicator = container.querySelector( '.rotate-indicator' );

		// Initialize ThreeJS components
		this.scene = null;
		this.camera = null;
		this.renderer = null;
		this.controls = null;
		this.model = null;
		this.hqModelLoaded = false;

		// Check if this is a mobile device
		this.isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			);

		// Animation properties
		this.bounceEnabled = true;
		this.bounceSpeed = 0.5;
		this.bounceHeight = 0.1;
		this.bounceTime = 0;
		this.initialY = 0;
		this.userInteracted = false;

		// Initialize the viewer
		this.init();
	}

	init() {
		// Create scene
		this.scene = new THREE.Scene();
		// Remove background color for transparency
		// this.scene.background = new THREE.Color( 0xffffff );

		// Create camera
		const aspectRatio =
			this.container.clientWidth / this.container.clientHeight;
		this.camera = new THREE.PerspectiveCamera( 45, aspectRatio, 0.1, 1000 );
		this.camera.position.set( 0, 0, 5 );

		// Create renderer with transparency enabled
		this.renderer = new THREE.WebGLRenderer( {
			canvas: this.canvas,
			antialias: true,
			alpha: true,
			powerPreference: 'high-performance', // Optimize for performance
		} );

		this.renderer.setSize(
			this.container.clientWidth,
			this.container.clientHeight
		);
		this.renderer.setPixelRatio( Math.min( window.devicePixelRatio, 2 ) ); // Limit pixel ratio for mobile
		this.renderer.colorSpace = THREE.SRGBColorSpace;
		// Set clear color with 0 alpha (fully transparent)
		this.renderer.setClearColor( 0x000000, 0 );

		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1.2; // Increase exposure

		// Add lights
		const ambientLight = new THREE.AmbientLight( 0xffffff, 1.2 );
		this.scene.add( ambientLight );

		const directionalLight = new THREE.DirectionalLight( 0xffffff, 2 );
		directionalLight.position.set( 1, 1, 1 );
		this.scene.add( directionalLight );

		// Add additional directional lights for better coverage
		const directionalLight2 = new THREE.DirectionalLight( 0xffffff, 1.5 );
		directionalLight2.position.set( -1, 1, -1 );
		this.scene.add( directionalLight2 );

		const directionalLight3 = new THREE.DirectionalLight( 0xffffff, 1 );
		directionalLight3.position.set( 0, -1, 0 );
		this.scene.add( directionalLight3 );

		// Add orbit controls
		this.controls = new OrbitControls( this.camera, this.canvas );
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.05;
		this.controls.screenSpacePanning = false;
		this.controls.minDistance = 1;
		this.controls.maxDistance = 10;

		// Detect user interaction to stop bounce animation
		this.controls.addEventListener( 'start', () => {
			this.userInteracted = true;

			// Only load high-quality model on desktop devices
			if ( ! this.isMobile ) {
				this.loadHighQualityModel();
			}

			// Hide rotation indicator after user interacts
			if ( this.rotateIndicator ) {
				setTimeout( () => {
					this.rotateIndicator.style.opacity = '0';
					setTimeout( () => {
						this.rotateIndicator.style.display = 'none';
					}, 500 );
				}, 1000 );
			}
		} );

		// Load the model
		this.loadModel();

		// Start animation loop
		this.animate();

		// Handle window resize
		window.addEventListener( 'resize', this.onResize.bind( this ) );
	}

	async loadModel() {
		try {
			// Dynamic imports to load only when needed
			const { GLTFLoader } = await import(
				'three/examples/jsm/loaders/GLTFLoader.js'
			);
			const { DRACOLoader } = await import(
				'three/examples/jsm/loaders/DRACOLoader.js'
			);

			const loader = new GLTFLoader();

			// Setup Draco loader
			const dracoLoader = new DRACOLoader();
			dracoLoader.setDecoderPath(
				'https://www.gstatic.com/draco/versioned/decoders/1.5.6/'
			);
			dracoLoader.setDecoderConfig( { type: 'js' } );
			loader.setDRACOLoader( dracoLoader );

			// Load initial model
			this.loadModelWithUrl( loader, this.modelUrl );
		} catch ( error ) {
			console.error( 'Failed to initialize model loader:', error );
			if ( this.loadingSpinner ) {
				this.loadingSpinner.innerHTML = '<p>Error loading model</p>';
			}
		}
	}

	loadModelWithUrl( loader, url ) {
		// Track if this is the high-quality model being loaded
		const isHQModel = url === this.hqModelUrl;

		// Store original camera position and controls if loading HQ model
		const originalCameraPos = isHQModel
			? this.camera.position.clone()
			: null;
		const originalControlsTarget = isHQModel
			? this.controls.target.clone()
			: null;

		loader.load(
			url,
			( gltf ) => {
				try {
					// If we already have a model in the scene, remove it
					if ( this.model ) {
						this.scene.remove( this.model );
					}

					// Model loaded successfully
					this.model = gltf.scene;

					// Center model
					const box = new THREE.Box3().setFromObject( this.model );
					const center = box.getCenter( new THREE.Vector3() );
					const size = box.getSize( new THREE.Vector3() );

					// Reset model position to center
					this.model.position.x = -center.x;
					this.model.position.y = -center.y;
					this.model.position.z = -center.z;

					// Save initial Y position for bounce animation
					this.initialY = this.model.position.y;

					// Only calculate new camera position for the first (low-res) model
					// or if we don't have a high-quality model being loaded
					if ( ! isHQModel ) {
						// Set camera position based on model size
						const maxDim = Math.max( size.x, size.y, size.z );
						const fov = this.camera.fov * ( Math.PI / 180 );
						let cameraZ = Math.abs(
							maxDim / 2 / Math.tan( fov / 2 )
						);

						// Set camera to show all of the model
						cameraZ *= 1.2; // Reduced margin to zoom in closer
						this.camera.position.z = cameraZ;

						// Update controls
						this.controls.target.set( 0, 0, 0 );
						this.controls.update();
					} else {
						// For HQ model, restore the original camera position and controls
						if ( originalCameraPos ) {
							this.camera.position.copy( originalCameraPos );
						}
						if ( originalControlsTarget ) {
							this.controls.target.copy( originalControlsTarget );
						}
						this.controls.update();
					}

					// Add model to scene
					this.scene.add( this.model );

					// Hide loading spinner
					if ( this.loadingSpinner ) {
						this.loadingSpinner.style.display = 'none';
					}

					// Add a subtle auto-rotation to show it's interactive
					setTimeout( () => {
						if ( ! this.userInteracted ) {
							this.controls.autoRotate = true;
							this.controls.autoRotateSpeed = 1.5;

							// Stop auto-rotation after 3 seconds
							setTimeout( () => {
								this.controls.autoRotate = false;
							}, 3000 );
						}
					}, 1000 );
				} catch ( error ) {
					console.error( 'Error processing loaded model:', error );
					if ( this.loadingSpinner ) {
						this.loadingSpinner.style.display = 'none';
					}
				}
			},
			( progress ) => {
				// Loading in progress
				// console.log(
				// 	'Loading model: ' +
				// 		( progress.loaded / progress.total ) * 100 +
				// 		'%'
				// );
			},
			( error ) => {
				// Error loading model
				console.error( 'Error loading 3D model:', error );
				if ( this.loadingSpinner ) {
					this.loadingSpinner.innerHTML =
						'<p>Error loading model</p>';
					this.loadingSpinner.style.display = 'flex';
				}
			}
		);
	}

	async loadHighQualityModel() {
		if (
			! this.shouldLoadHq ||
			this.hqModelLoaded ||
			this.hqModelUrl === this.modelUrl ||
			this.isMobile // Skip HQ loading on mobile
		)
			return;

		try {
			// Show loading indicator for HQ model
			if ( this.loadingSpinner ) {
				this.loadingSpinner.style.display = 'flex';
			}

			// Load the high quality version of the model
			const { GLTFLoader } = await import(
				'three/examples/jsm/loaders/GLTFLoader.js'
			);
			const { DRACOLoader } = await import(
				'three/examples/jsm/loaders/DRACOLoader.js'
			);

			const loader = new GLTFLoader();
			const dracoLoader = new DRACOLoader();
			dracoLoader.setDecoderPath(
				'https://www.gstatic.com/draco/versioned/decoders/1.5.6/'
			);
			dracoLoader.setDecoderConfig( { type: 'js' } );
			loader.setDRACOLoader( dracoLoader );

			this.loadModelWithUrl( loader, this.hqModelUrl );
			this.hqModelLoaded = true;
		} catch ( error ) {
			console.error( 'Failed to load high-quality model:', error );
			if ( this.loadingSpinner ) {
				this.loadingSpinner.style.display = 'none';
			}
		}
	}

	animate() {
		requestAnimationFrame( this.animate.bind( this ) );

		// Bounce animation when model is loaded and user hasn't interacted
		if ( this.model && this.bounceEnabled && ! this.userInteracted ) {
			this.bounceTime += 0.016; // Approximately 60fps
			const bounceOffset =
				Math.sin( this.bounceTime * this.bounceSpeed ) *
				this.bounceHeight;
			this.model.position.y = this.initialY + bounceOffset;
		}

		// Update controls
		if ( this.controls ) {
			this.controls.update();
		}

		// Render scene
		this.renderer.render( this.scene, this.camera );
	}

	onResize() {
		if ( ! this.camera || ! this.renderer ) return;

		// Update aspect ratio
		const width = this.container.clientWidth;
		const height = this.container.clientHeight;

		this.camera.aspect = width / height;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize( width, height );
	}
}

// Initialize all model viewers on the page
document.addEventListener( 'DOMContentLoaded', () => {
	const containers = document.querySelectorAll( '#model-container' );

	// Create intersection observer to lazy load models
	const observer = new IntersectionObserver(
		( entries, obs ) => {
			entries.forEach( ( entry ) => {
				// Only initialize when model container is visible or about to be visible
				if ( entry.isIntersecting ) {
					try {
						new ModelViewer( entry.target );
						// Stop observing after initializing
						obs.unobserve( entry.target );
					} catch ( error ) {
						console.error(
							'Failed to initialize model viewer:',
							error
						);
						const errorMessage =
							entry.target.querySelector( '.loading-spinner' );
						if ( errorMessage ) {
							errorMessage.innerHTML =
								'<p>Error loading model</p>';
						}
					}
				}
			} );
		},
		{
			rootMargin: '100px 0px', // Start loading when within 100px of viewport
			threshold: 0.01,
		}
	);

	// Observe all model containers
	containers.forEach( ( container ) => {
		observer.observe( container );
	} );
} );
